import Swal from 'sweetalert2'
import { ref, computed, watch } from 'vue'
import { cloneDeep } from 'lodash'
import { diff } from 'deep-diff'
import { useCheckoutStore } from '../stores/store.js'
import { captureCartError, CheckoutAction, ErrorLevel } from './sentry'
import { cartApi } from './cartApi.js'
import { currentCartEpoch, isStaleCartEpoch, emptyGuestCart } from './cartRecovery.js'
import { importLegacyCartIfPresent } from './legacyCartImport.js'
import { createUnloadGuard, initialCartSyncStatus, offlineRetryDelay } from './cartOffline.js'

// The cart lives on the server (plan IRO-3676). This module keeps an in-memory copy for the page, sends edits
// with cartApi, and adopts what the server answers (lines, prices, totals, version). Nothing is written to
// browser storage; the old `cart` key is only ever read by legacyCartImport.js.

// Initialize store lazily when functions are called rather than at import time
const getCheckout = () => useCheckoutStore()

// Reactive so the banner component can show it. One cart module is live at a time.
export const cartSyncStatus = ref(initialCartSyncStatus())
const unloadGuard = createUnloadGuard(() => cartSyncStatus.value)
let unloadGuardInstalled = false
function installUnloadGuard() {
	if (unloadGuardInstalled || typeof window === 'undefined') return
	window.addEventListener('beforeunload', unloadGuard)
	unloadGuardInstalled = true
}

// One listener for the page; each initCart (login and logout rebuild it) swaps in its own refresh.
let refreshOnFocus = null
let visibilityInstalled = false
function installVisibilityRefresh() {
	if (visibilityInstalled || typeof document === 'undefined') return
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') refreshOnFocus?.()
	})
	visibilityInstalled = true
}

/**
 * Validates that a cart item has the minimum required fields to be usable
 * @param {Object} item - A cart item object
 * @returns {boolean} True if the item is valid
 */
let productRequiresVariant = (item) => {
	const product = typeof item?.item === 'object' ? item.item : null
	if (!product) return false
	return !!(product.optionSetId || product.inventoryTracking === 'variant' || product.variants?.length > 1)
}

let cartItemHasVariant = (item) => {
	if (!item?.variant) return false
	if (typeof item.variant === 'string') return !!item.variant
	return !!(item.variant?.id || item.variant?.bcId)
}

let isValidCartItem = (item) => {
	if (!item || typeof item !== 'object') return false
	// Must have an item reference (either populated object with id, or just the id string)
	const hasItem = item.item && (typeof item.item === 'string' || item.item?.id)
	const hasVariant = !productRequiresVariant(item) || cartItemHasVariant(item)
	// Must have a positive quantity
	const hasQuantity = typeof item.quantity === 'number' && item.quantity > 0
	return hasItem && hasVariant && hasQuantity
}

/**
 * Filters out null, undefined, or invalid cart items
 * @param {Array} cartItems - Array of cart items
 * @returns {Array} Sanitized array with only valid items
 */
let sanitizeCartItems = (cartItems) => {
	if (!Array.isArray(cartItems)) return []
	return cartItems.filter(isValidCartItem)
}

let simplifyCartItems = (cartItems) => {
	return (cartItems || []).map((ci) => ({
		quantity: ci.quantity,
		item: ci.item?.id || ci.item,
		variant: ci.variant?.id || ci.variant,
		optional: ci?.optional?.coveredProductCode || null,
		queryID: ci?.queryID || null,
	}))
}

/** The store shape for what the server returned. Totals, ids and the version are the server's, never the page's. */
export function cartFromServer(view) {
	const base = emptyGuestCart()
	if (!view) return base
	return {
		...base,
		...view,
		cartItems: sanitizeCartItems(view.cartItems),
		certificates: view.certificates || [],
		coupon: view.coupon || { code: '' },
		couponType: view.couponType || false,
		certificate: view.certificate || null,
	}
}

const WARNING_TEXT = {
	quantity_clamped: (w) => `A quantity was reduced to what is available${w.to ? ` (${w.to})` : ''}.`,
	out_of_stock: () => 'An item is out of stock and was removed from your cart.',
	product_unavailable: () => 'An item is no longer available and was removed from your cart.',
	purchasing_disabled: () => 'An item can no longer be purchased and was removed from your cart.',
	cart_truncated: () => 'Your cart was too large to merge completely.',
}

/** Shopper-facing text for the server's structured warnings (coupon problems are shown separately). */
export function describeCartWarnings(warnings) {
	const list = Array.isArray(warnings) ? warnings : []
	const lines = []
	for (const w of list) {
		const text = WARNING_TEXT[w?.code]?.(w)
		if (text && !lines.includes(text)) lines.push(text)
	}
	return lines
}

export const couponWarning = (warnings) => (Array.isArray(warnings) ? warnings.find((w) => w?.code === 'coupon_invalid' || w?.code === 'coupon_rejected') : null)

const initCart = async (user, api) => {
	const epoch = currentCartEpoch()
	installUnloadGuard()
	cartSyncStatus.value = initialCartSyncStatus()

	// One request decides the cart: the session (member) or the guest cookie says whose it is.
	let loaded = await cartApi.get()
	let cartView = loaded.ok ? loaded.cart : null
	if (!loaded.ok) {
		captureCartError(loaded.error || loaded.message || 'Cart load failed', {
			action: CheckoutAction.SYNC_CART,
			cart: null,
			user,
			level: loaded.network ? ErrorLevel.WARNING : ErrorLevel.ERROR,
			extra: { step: 'initCart.get', status: loaded.status, code: loaded.code },
		})
		if (loaded.network) cartSyncStatus.value = { ...cartSyncStatus.value, offline: true }
	}

	// A browser-stored cart from before the rollout is imported once and its key removed after the server's 200.
	if (loaded.ok) {
		try {
			const imported = await importLegacyCartIfPresent({ cartApi })
			if (imported.status === 'imported' || imported.status === 'already_imported') cartView = imported.cart || cartView
			// Lines the server had to drop or clamp are explained; the old key is gone, so silence would look like loss.
			const importNotes = describeCartWarnings(imported.warnings)
			if (importNotes.length) Swal.fire({ icon: 'info', title: 'Your saved cart was updated', html: importNotes.join('<br>'), confirmButtonColor: '#e59789' })
			if (imported.status === 'unverified') {
				captureCartError('Legacy cart import not readable back; kept browser copy', {
					action: CheckoutAction.SYNC_CART,
					cart: null,
					user,
					level: ErrorLevel.WARNING,
					extra: { step: 'initCart.import', code: imported.code },
				})
			}
			if (imported.status === 'failed') {
				captureCartError(imported.error || 'Legacy cart import failed', {
					action: CheckoutAction.SYNC_CART,
					cart: null,
					user,
					level: ErrorLevel.WARNING,
					extra: { step: 'initCart.import', code: imported.code },
				})
			}
		} catch (error) {
			captureCartError(error, { action: CheckoutAction.SYNC_CART, cart: null, user, level: ErrorLevel.WARNING, extra: { step: 'initCart.import' } })
		}
	}

	const savingCart = ref(false)
	const cart = ref(cartFromServer(cartView))
	const originalCart = ref(cloneDeep(cart.value))
	// Edits made while a save was in flight: they must not be overwritten by that save's answer.
	let editedDuringSave = false
	let timer
	let retryTimer

	const itemsCount = computed(() => cart.value.cartItems?.reduce((acc, ci) => acc + (ci?.quantity || 0), 0))
	const certificatesCount = computed(() => cart.value?.certificates?.reduce?.((acc, cert) => acc + (cert?.quantity || 0), 0)) || 0
	const cartTotalItems = computed(() => itemsCount.value + certificatesCount.value)
	const cartItemsSubtotal = computed(() => cart.value.cartItems?.reduce((acc, ci) => acc + ci.quantity * (ci?.variant?.currentPrice || ci?.item?.currentPrice || 0), 0))
	const cartCertificatesSubtotal = computed(() => cart.value.certificates?.reduce((acc, cert) => acc + cert.quantity * (cert?.amount || 0), 0))
	const cartSubtotal = computed(() => cartItemsSubtotal.value + cartCertificatesSubtotal.value)
	const texasStateSalesTax = computed(() => cartSubtotal.value * 0.0825)
	const isDigitalOnly = computed(() => !cart.value.cartItems?.length && !!cart.value?.certificates?.length)

	const cartTotal = computed(() => {
		const subtotal = cartSubtotal.value
		const tax = texasStateSalesTax.value
		return subtotal + tax
	})

	const cartItems = computed(() => cart.value.cartItems)
	const certificates = computed(() => cart.value.certificates) // certificate(s) in the cart being purchased

	const isEmpty = () => !cart.value?.cartItems?.length && !cart.value?.certificates?.length

	function finishSave(checkout) {
		savingCart.value = false
		checkout.updateLoading(false)
	}

	/** Replace the page's cart with the server's. Lines the shopper edited meanwhile are kept; only server-owned fields move. */
	function adopt(view) {
		const next = cartFromServer(view)
		if (editedDuringSave) {
			for (const key of ['id', 'bcId', 'version', 'subTotal', 'tax', 'total', 'couponDiscount', 'couponType', 'certificateDiscount', 'isDigitalOnly']) cart.value[key] = next[key]
			originalCart.value = cloneDeep({ ...next, cartItems: next.cartItems })
			return
		}
		cart.value = next
		originalCart.value = cloneDeep(next)
	}

	function scheduleOfflineRetry() {
		clearTimeout(retryTimer)
		const delay = offlineRetryDelay(cartSyncStatus.value.retryAttempt)
		cartSyncStatus.value = { unsaved: true, offline: true, retryAttempt: cartSyncStatus.value.retryAttempt + 1 }
		retryTimer = setTimeout(() => {
			Promise.resolve(saveCart({ background: true })).catch(() => {})
		}, delay)
	}

	async function saveCart({ background = false } = {}) {
		if (isStaleCartEpoch(epoch)) return
		const checkout = getCheckout()
		clearTimeout(retryTimer)
		// A visitor who never had a cart and has nothing in it has nothing to store.
		if (isEmpty() && !cart.value.id) {
			originalCart.value = cloneDeep(cart.value)
			savingCart.value = false
			return
		}
		if (!background) checkout.updateLoading(true)
		savingCart.value = true
		editedDuringSave = false

		const res = await cartApi.put(cart.value, cart.value.version)
		if (isStaleCartEpoch(epoch)) {
			finishSave(checkout)
			return
		}

		if (res.ok) {
			cartSyncStatus.value = initialCartSyncStatus()
			const pending = editedDuringSave
			if (res.cart) adopt(res.cart)
			finishSave(checkout)
			const coupon = couponWarning(res.warnings)
			if (coupon) Swal.fire('Invalid Coupon', coupon.message || 'This coupon could not be applied to your cart.', 'warning')
			const notes = describeCartWarnings(res.warnings)
			if (notes.length) Swal.fire({ icon: 'info', title: 'Your cart was updated', html: notes.join('<br>'), confirmButtonColor: '#e59789' })
			editedDuringSave = false
			if (pending) debounceSave.value()
			return
		}

		// The request never reached the server: keep the edit in memory, retry with backoff, say so.
		if (res.network) {
			finishSave(checkout)
			scheduleOfflineRetry()
			return
		}

		// Another tab or device changed the cart first. The answer carries the current one.
		if (res.code === 'version_conflict' && res.cart) {
			editedDuringSave = false
			adopt(res.cart)
			finishSave(checkout)
			Swal.fire({ icon: 'info', title: 'Cart updated', text: res.message || 'Your cart changed in another window. We loaded the latest version.', confirmButtonColor: '#e59789' })
			return
		}

		// BigCommerce refused a line (usually stock). Drop that line, keep the rest, save again.
		if (res.code === 'bc_refused' && (res.productBcId || /insufficient stock|can only purchase a maximum/i.test(res.message || ''))) {
			const before = cart.value.cartItems?.length || 0
			const name = res.productName
			if (res.productBcId) cart.value.cartItems = cart.value.cartItems.filter((ci) => ci.item?.bcId !== res.productBcId)
			else if (name) cart.value.cartItems = cart.value.cartItems.filter((ci) => (ci.item?.name || '') !== name)
			const removed = (cart.value.cartItems?.length || 0) < before
			if (!removed) {
				// Could not tell which line; fall back to the last cart the server accepted.
				cart.value = cloneDeep(originalCart.value)
				finishSave(checkout)
				Swal.fire('Insufficient Stock Available', res.message || 'An error occurred while updating your cart.', 'warning')
				return
			}
			savingCart.value = false
			Swal.fire('Insufficient Stock Available', `<b>${name || 'An item'}</b> has insufficient stock and has been removed from your cart.`, 'warning')
			debounceSave.value()
			return
		}

		// Anything else: revert to the last cart the server accepted so the page matches the server.
		const resetCount = checkout.getCartResetCount + 1
		checkout.updateCartResetCount(resetCount > 2 ? 0 : resetCount)
		captureCartError(res.error || res.message || 'An error occurred while updating your cart', {
			action: CheckoutAction.SAVE_CART,
			cart: cart.value,
			user,
			warnings: res.message,
			level: ErrorLevel.ERROR,
			extra: { status: res.status, code: res.code, cartResetCount: resetCount, cartItemsCount: cart.value?.cartItems?.length, currentUrl: window.location.href },
		})
		if (res.cart) adopt(res.cart)
		else cart.value = cloneDeep(originalCart.value)
		originalCart.value = cloneDeep(cart.value)
		finishSave(checkout)
		Swal.fire('Cart Error', res.message || 'An error occurred while updating your cart.', 'warning')
	}

	const debounceSave = computed(() => {
		return (...args) => {
			savingCart.value = true

			// Add things here that you want to be immediately invalidated on change
			cart.value.tax = null
			cart.value.total = null

			clearTimeout(timer)
			timer = setTimeout(() => {
				Promise.resolve(saveCart.apply(this, args)).catch((error) => {
					captureCartError(error, { action: CheckoutAction.SAVE_CART, cart: cart.value, user, level: ErrorLevel.ERROR, extra: { step: 'debounceSave' } })
					savingCart.value = false
					try {
						getCheckout().updateLoading(false)
					} catch {
						// checkout store can be unavailable during teardown
					}
				})
			}, 1200)
		}
	})

	function shouldSkipWatch(next) {
		if (isStaleCartEpoch(epoch)) return true
		const checkout = getCheckout()
		if (checkout?.validatingCart) return true
		// An edit during a save is remembered so the answer does not overwrite it.
		if (savingCart.value) {
			editedDuringSave = true
			return true
		}
		// Skip cart sync after successful checkout (cart was intentionally cleared)
		// But if cart has items again, user is shopping - clear the flag
		if (checkout?.checkoutSuccess) {
			if (next?.length) checkout.checkoutSuccess = false
			else return true
		}
		return false
	}

	watch(
		cartItems,
		(ci) => {
			if (shouldSkipWatch(ci)) return
			// Check to see if anything actually changed:
			const differences = diff(simplifyCartItems(ci), simplifyCartItems(originalCart.value.cartItems))
			if (differences?.length) {
				getCheckout().bcOrderId = null
				debounceSave.value()
			}
		},
		{ deep: true },
	)

	watch(
		certificates,
		(certs) => {
			if (shouldSkipWatch(certs)) return
			// Compare against originalCart.certificates so a full cart.value
			// replacement (adopting the server's answer) doesn't fire a spurious save.
			const differences = diff(certs || [], originalCart.value?.certificates || [])
			if (!differences?.length) return
			getCheckout().bcOrderId = null
			debounceSave.value()
		},
		{ deep: true },
	)

	// A cart changed in another tab or on another device is picked up when this one comes back into view.
	refreshOnFocus = async () => {
		if (isStaleCartEpoch(epoch) || savingCart.value || cartSyncStatus.value.unsaved) return
		const fresh = await cartApi.get()
		if (fresh.ok && !savingCart.value && !cartSyncStatus.value.unsaved && (fresh.cart?.version || 0) !== (cart.value.version || 0)) adopt(fresh.cart)
	}
	installVisibilityRefresh()

	// Note: validateInventory (triggered by App.vue when cartInitialized becomes true) checks stock and then
	// calls saveCart when it changed the cart.

	/** Mark the page's cart as already stored (the caller replaced lines locally and has saved or is about to). */
	function syncOriginalCart() {
		originalCart.value = cloneDeep(cart.value)
	}

	return {
		cart,
		itemsCount,
		certificatesCount,
		cartTotalItems,
		cartSubtotal,
		savingCart,
		saveCart,
		syncOriginalCart,
		texasStateSalesTax,
		cartTotal,
		originalCart,
		isDigitalOnly,
	}
}

export default initCart
